import { Box, Slider, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { ShowDialogProps } from 'src/show-dialog'
import { getPlayableVideoBlob } from 'src/utils/rovVideoPreview'
import { captureFrameFromVideoElement } from 'src/utils/rovVideoScreenshot'
import { GenericDialogLayout } from './GenericDialogLayout'
import { MainButton } from './MainButton'
import { clampToDuration, formatSecondsAsClock } from './VideoFramePickerDialog.action'

export interface VideoFramePickerResult {
  blob: Blob
  currentTime: number
}

export interface VideoFramePickerDialogProps extends ShowDialogProps<VideoFramePickerResult | undefined> {
  file: File
  initialSeconds?: number
}

export const VideoFramePickerDialog: React.FC<VideoFramePickerDialogProps> = ({ pop, file, initialSeconds }) => {
  const { t } = useTranslation(['rov'])
  const videoRef = useRef<HTMLVideoElement>(null)
  const [objectUrl, setObjectUrl] = useState<string>()
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isReady, setIsReady] = useState(false)
  const [isSeeking, setIsSeeking] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  useEffect(() => {
    let cancelled = false
    let url: string | undefined
    getPlayableVideoBlob(file)
      .then(blob => {
        if (cancelled) return
        url = URL.createObjectURL(blob)
        setObjectUrl(url)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [file])

  const handleLoadedMetadata = () => {
    const video = videoRef.current
    if (!video) return
    setDuration(video.duration)
    const seeded = clampToDuration(initialSeconds ?? 1, video.duration)
    video.currentTime = seeded
    setCurrentTime(seeded)
    setIsReady(true)
  }

  const handleTimeUpdate = () => {
    if (isSeeking) return
    setCurrentTime(videoRef.current?.currentTime ?? 0)
  }

  const handleSliderChange = (_event: Event, value: number | number[]) => {
    const video = videoRef.current
    if (!video) return
    const nextTime = Array.isArray(value) ? value[0] : value
    video.pause()
    setIsSeeking(true)
    video.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  const handleGenerateScreenshot = async () => {
    const video = videoRef.current
    if (!video) return

    setIsGenerating(true)
    const blob = await captureFrameFromVideoElement(video, { maxWidth: 1280 })
    setIsGenerating(false)

    if (!blob) {
      toast.warning(t('screenshot-capture-failed', { ns: 'rov' }))
      return
    }
    pop({ blob, currentTime: video.currentTime })
  }

  return (
    <GenericDialogLayout
      title={t('pick-video-frame-title', { ns: 'rov' })}
      pop={pop}
      actions={[
        <MainButton
          key="generate-screenshot-btn"
          onClick={handleGenerateScreenshot}
          disabled={!isReady || isSeeking || loadError || isGenerating}
        >
          {t('generate-screenshot', { ns: 'rov' })}
        </MainButton>,
      ]}
    >
      <Box
        sx={{
          width: '100%',
          aspectRatio: '16/9',
          backgroundColor: 'common.black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!objectUrl && !loadError && (
          <Typography variant="body2" sx={{ color: 'common.white' }}>
            {t('preparing-video-preview', { ns: 'rov' })}
          </Typography>
        )}
        {objectUrl && (
          <video
            ref={videoRef}
            src={objectUrl}
            controls
            playsInline
            style={{ width: '100%', height: '100%' }}
            onLoadedMetadata={handleLoadedMetadata}
            onSeeked={() => setIsSeeking(false)}
            onTimeUpdate={handleTimeUpdate}
            onError={() => setLoadError(true)}
          />
        )}
      </Box>
      {loadError ? (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {t('frame-picker-load-error', { ns: 'rov' })}
        </Typography>
      ) : (
        <>
          <Slider
            value={currentTime}
            min={0}
            max={duration || 0}
            step={0.1}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            valueLabelFormat={formatSecondsAsClock}
            disabled={!isReady}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            {t('pick-frame-instructions', { ns: 'rov' })}
          </Typography>
        </>
      )}
    </GenericDialogLayout>
  )
}
